import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Plus, FileText, ToggleLeft, ToggleRight, Trash2, Pencil, Search, ClipboardList, Copy, CheckCircle2, XCircle, Loader2 } from 'lucide-react'
import { formsApi, categoriesApi } from '@/lib/api'
import { api } from '@/lib/api-client'
import { useAuth } from '@/contexts/AuthContext'
import { formatDate } from '@/lib/utils'
import toast from 'react-hot-toast'

// ── Copy Form Modal ────────────────────────────────────────────────────────────
function CopyFormModal({ form, onClose }: { form: any; onClose: () => void }) {
  const { user } = useAuth()
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [results, setResults] = useState<any[] | null>(null)

  // Fetch accessible tenants (same user, other tenants)
  const { data: tenantsData, isLoading: tenantsLoading } = useQuery({
    queryKey: ['my-tenants-for-copy'],
    queryFn: () => api.get<{ tenants: { id: number; name: string; slug: string }[] }>('/api/auth/my-tenants'),
  })
  const otherTenants = (tenantsData?.data?.tenants ?? []).filter(
    (t: any) => t.slug !== (user as any)?.tenant_slug && t.id !== (user as any)?.tenant_id
  )

  const copyMut = useMutation({
    mutationFn: () =>
      api.post<{ results: any[] }>(`/api/forms/${form.id}/copy`, { target_tenant_ids: selectedIds }),
    onSuccess: (res: any) => setResults(res.data.results),
    onError: (e: any) => toast.error(e?.response?.data?.detail ?? 'Erro ao copiar'),
  })

  function toggle(id: number) {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-xl bg-white shadow-xl">
        <div className="border-b p-5">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Copy className="h-5 w-5 text-primary-600" /> Copiar formulário
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Copiar <strong>{form.name}</strong> para outros tenants onde tem acesso.
            O formulário será criado como <em>inativo</em> no destino.
          </p>
        </div>

        <div className="p-5">
          {results ? (
            // Show results
            <div className="space-y-2">
              <p className="font-medium text-gray-700 mb-3">Resultado da cópia:</p>
              {results.map((r: any) => (
                <div key={r.tenant_id} className={`flex items-center gap-3 rounded-lg p-3 ${r.status === 'copied' ? 'bg-green-50' : 'bg-red-50'}`}>
                  {r.status === 'copied'
                    ? <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
                    : <XCircle className="h-5 w-5 text-red-600 shrink-0" />}
                  <div>
                    <p className="font-medium text-sm">{r.tenant_name}</p>
                    <p className="text-xs text-gray-500">
                      {r.status === 'copied' ? `Copiado → form #${r.form_id}` : 'Sem permissão ou erro'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : tenantsLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary-600" /></div>
          ) : otherTenants.length === 0 ? (
            <p className="py-6 text-center text-sm text-gray-500">
              Não há outros tenants acessíveis com as suas credenciais.
            </p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {otherTenants.map((t: any) => (
                <label key={t.id ?? t.slug} className="flex items-center gap-3 cursor-pointer rounded-lg border border-gray-200 p-3 hover:bg-gray-50">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 text-primary-600"
                    checked={selectedIds.includes(t.id)}
                    onChange={() => toggle(t.id)}
                  />
                  <div>
                    <p className="font-medium text-sm text-gray-900">{t.name}</p>
                    <p className="text-xs text-gray-500">{t.slug}</p>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="flex gap-3 border-t p-5">
          <button className="btn btn-secondary flex-1" onClick={onClose}>
            {results ? 'Fechar' : 'Cancelar'}
          </button>
          {!results && (
            <button
              className="btn btn-primary flex-1 flex items-center justify-center gap-2"
              disabled={selectedIds.length === 0 || copyMut.isPending}
              onClick={() => copyMut.mutate()}
            >
              {copyMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Copy className="h-4 w-4" />}
              Copiar para {selectedIds.length > 0 ? `${selectedIds.length} tenant(s)` : '...'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default function FormsPage() {
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState('')
  const [copyForm, setCopyForm] = useState<any | null>(null)
  const queryClient = useQueryClient()

  const { data: catsData } = useQuery({
    queryKey: ['form-categories'],
    queryFn: () => categoriesApi.list(),
    staleTime: 60_000,
  })
  const categories = (catsData?.data ?? []) as { id: number; name: string }[]

  const { data, isLoading } = useQuery({
    queryKey: ['forms', search, category],
    queryFn: () => formsApi.list({ page: 1, page_size: 50, search: search || undefined, category: category || undefined }),
    staleTime: 10_000,
  })

  const forms = data?.data?.items ?? []
  const total = data?.data?.total ?? 0

  const toggleMut = useMutation({
    mutationFn: (id: number) => formsApi.toggle(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['forms'] }),
  })

  const deleteMut = useMutation({
    mutationFn: (id: number) => formsApi.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['forms'] }),
  })

  return (
    <div>
      {copyForm && <CopyFormModal form={copyForm} onClose={() => setCopyForm(null)} />}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Formulários</h1>
          <p className="mt-1 text-sm text-gray-600">
            {total} formulário{total !== 1 ? 's' : ''} configurado{total !== 1 ? 's' : ''}
          </p>
        </div>
        <Link to="/forms/new" className="btn btn-primary flex items-center gap-2">
          <Plus className="h-4 w-4" /> Novo Formulário
        </Link>
      </div>

      {/* Filters */}
      <div className="mt-4 flex gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input className="input pl-9 w-full" placeholder="Pesquisar..." value={search}
            onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="input w-44" value={category} onChange={e => setCategory(e.target.value)}>
          <option value="">Todas as categorias</option>
          {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="mt-6 card overflow-x-auto">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
          </div>
        ) : forms.length === 0 ? (
          <div className="py-16 text-center text-gray-400">
            <FileText className="mx-auto mb-3 h-10 w-10 opacity-40" />
            <p>Sem formulários. Cria o primeiro.</p>
          </div>
        ) : (
          <table className="table min-w-full">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Categoria</th>
                <th>Campos</th>
                <th>Submissões</th>
                <th>Estado</th>
                <th>Atualizado</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {forms.map((form: any) => (
                <tr key={form.id}>
                  <td>
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-100">
                        <FileText className="h-5 w-5 text-primary-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{form.name}</p>
                        {form.description && (
                          <p className="text-xs text-gray-400 truncate max-w-xs">{form.description}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    {form.category ? (
                      <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-700">{form.category}</span>
                    ) : <span className="text-gray-400 text-xs">—</span>}
                  </td>
                  <td className="text-center text-sm text-gray-600">{form.fields?.length ?? 0}</td>
                  <td className="text-center text-sm text-gray-600">{form.submission_count ?? 0}</td>
                  <td>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${form.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {form.is_active ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="text-xs text-gray-500">{formatDate(form.updated_at)}</td>
                  <td>
                    <div className="flex items-center gap-1">
                      <Link to={`/forms/${form.id}/submissions`}
                        className="rounded p-1 text-purple-600 hover:bg-purple-50"
                        title="Ver submissões">
                        <ClipboardList className="h-4 w-4" />
                      </Link>
                      <Link to={`/forms/${form.id}/edit`}
                        className="rounded p-1 text-blue-600 hover:bg-blue-50">
                        <Pencil className="h-4 w-4" />
                      </Link>
                      <button onClick={() => toggleMut.mutate(form.id)}
                        className="rounded p-1 text-gray-500 hover:bg-gray-100">
                        {form.is_active
                          ? <ToggleRight className="h-4 w-4 text-green-600" />
                          : <ToggleLeft className="h-4 w-4" />}
                      </button>
                      <button
                        onClick={() => { if (confirm(`Eliminar "${form.name}"?`)) deleteMut.mutate(form.id) }}
                        className="rounded p-1 text-red-600 hover:bg-red-50">
                        <Trash2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setCopyForm(form)}
                        className="rounded p-1 text-indigo-600 hover:bg-indigo-50"
                        title="Copiar para outro tenant">
                        <Copy className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
